import { useState } from 'react';

import * as Sentry from '@sentry/browser';

import { startRegister } from '@hotjar/webapp/domains/registration/api/saml-invitation';
import type {
  SamlSsoModelInvitation,
  SamlSsoModelSuccessfulResponse,
} from '@hotjar/webapp/domains/registration/model';
import type {
  SamlSsoResponseFieldsValidationErrors,
  SamlSsoResponseSuccess,
} from '@hotjar/webapp/domains/registration/dto';
import { samlResponseInvitationDtoToModel } from '@hotjar/webapp/domains/registration/mapper';

export enum STATUS {
  INITIAL,
  LOADING,
  ERROR,
  SUCCESS,
}

type RegisterSamlSsoUser = (invitation: SamlSsoModelInvitation) => Promise<void>;

type SamlSsoRegisterHook = {
  data: {
    nextUrl: string;
  } | null;
  fieldErrors: null | SamlSsoResponseFieldsValidationErrors;
  apiErrorMessage: string | null;
  status: STATUS;
  registerSamlSsoUser: RegisterSamlSsoUser;
};

const genericError = 'Oops. Something went wrong.';

export function useInvitationSamlSsoApi(): SamlSsoRegisterHook {
  const [status, setStatus] = useState(STATUS.INITIAL);
  const [data, setData] = useState<SamlSsoModelSuccessfulResponse | null>(null);
  const [fieldErrors, setFieldErrors] = useState<SamlSsoResponseFieldsValidationErrors | null>(
    null,
  );
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const clearErrors = () => setFieldErrors(null);

  const registerSamlSsoUser: RegisterSamlSsoUser = async (invitation) => {
    try {
      setStatus(STATUS.LOADING);
      clearErrors();

      const invitationResult = await startRegister(invitation);

      setStatus(STATUS.SUCCESS);

      if (invitationResult.data) {
        setData(samlResponseInvitationDtoToModel(invitationResult.data as SamlSsoResponseSuccess));
      }
    } catch (errorResponse) {
      // @ts-expect-error fix catch error type
      const { error, errors } = errorResponse?.response?.data || {};
      if (error) {
        setApiErrorMessage(error);
      } else if (errors) {
        setFieldErrors({ errors });
      } else {
        setApiErrorMessage(genericError);
        Sentry.captureMessage('Error signing up user using Saml Sso', Sentry.Severity.Error);
        Sentry.captureException(errorResponse);
      }
      setStatus(STATUS.ERROR);
    }
  };

  return { status, data, fieldErrors, apiErrorMessage, registerSamlSsoUser };
}
